// src/store/slices/characterDraftSlice.js
// Единый слайс черновика персонажа.
// Отвечает ТОЛЬКО за состояние редактирования (создание или правка).
// Сохранение в БД по-прежнему через gameSlice (createCharacter/updateCharacter).
//
// Источник истины для:
//   - активного шага мастера
//   - черновика (name, background, race, template, temperament, gender, skills, ...)
//   - ошибок валидации по шагам и в редакторе
//   - флага isDirty в редакторе
//
// Никаких локальных useState в компонентах создания/редактирования не остаётся.

import { createSlice } from '@reduxjs/toolkit';

// ---------- Константы ----------

export const CREATION_STEPS = [
  'race',
  'template',
  'identity',
  'skills',
  'finalize',
];

export const MODE_CREATE = 'create';
export const MODE_EDIT = 'edit';

// ---------- Хелперы нормализации ----------

const LEGEND_FIELDS = [
  'title',
  'lineage',
  'birthplace',
  'childhood_memory',
  'motivation',
  'background',
  'description',
];

const emptyLegend = () => ({
  title: '',
  lineage: '',
  birthplace: '',
  childhood_memory: '',
  motivation: '',
  background: '',
  description: '',
});

/**
 * Приводит любой legend/background к объекту известной формы.
 * Принимает строку, объект, объект с .legend, null/undefined/массив.
 */
export const normalizeLegend = (raw) => {
  const base = emptyLegend();
  if (!raw) return base;

  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    return { ...base, description: trimmed, background: trimmed };
  }

  if (Array.isArray(raw)) return base;

  if (typeof raw === 'object') {
    const src =
      raw.legend && typeof raw.legend === 'object' ? raw.legend : raw;

    LEGEND_FIELDS.forEach((field) => {
      if (typeof src[field] === 'string' && src[field].trim()) {
        base[field] = src[field];
      }
    });

    if (!base.description) {
      base.description = base.background || base.childhood_memory || '';
    }
    if (!base.background) {
      base.background = base.description || '';
    }
  }

  return base;
};

export const isLegendEmpty = (legend) => {
  const norm = normalizeLegend(legend);
  return !LEGEND_FIELDS.some(
    (field) => typeof norm[field] === 'string' && norm[field].trim().length > 0
  );
};

export const legendToString = (legend) => {
  const l = normalizeLegend(legend);
  const parts = [];
  if (l.title) parts.push(`Звание: ${l.title}`);
  if (l.lineage) parts.push(`Род: ${l.lineage}`);
  if (l.birthplace) parts.push(`Родина: ${l.birthplace}`);
  if (l.childhood_memory) parts.push(`Память детства: "${l.childhood_memory}"`);
  if (l.motivation) parts.push(`Мотивация: ${l.motivation}`);
  if (l.background) parts.push(`Предыстория: ${l.background}`);
  else if (l.description) parts.push(`Описание: ${l.description}`);
  return parts.join('\n\n');
};

export const legendLength = (legend) => {
  const l = normalizeLegend(legend);
  return LEGEND_FIELDS.reduce(
    (sum, field) =>
      sum + (typeof l[field] === 'string' ? l[field].trim().length : 0),
    0
  );
};

/** Пустой черновик. */
const createEmptyDraft = () => ({
  id: null,                 // null = ещё не сохранён (режим создания)
  name: '',
  gender: 'neutral',
  race: null,               // объект расы (не id) для UI
  race_id: null,            // id для payload
  character_template: null, // объект шаблона для UI
  template_id: null,        // id для payload
  temperament: null,
  background: emptyLegend(),
  skills: [],
  traits: [],
  preferences: [],
  phobias: [],
  appearance: {},
});

/** Собирает черновик из сохранённого персонажа (для редактора). */
const draftFromCharacter = (character) => {
  if (!character || typeof character !== 'object') return createEmptyDraft();

  const raceId = character.race_id || character.race;
  const templateId = character.template_id || character.template;

  return {
    id: character.id || character.image_bit_id || null,
    name: character.name || '',
    gender: character.gender || 'neutral',
    race: character.race && typeof character.race === 'object' ? character.race : null,
    race_id: typeof raceId === 'string' || typeof raceId === 'number' ? raceId : null,
    character_template:
      character.character_template && typeof character.character_template === 'object'
        ? character.character_template
        : character.template_info && typeof character.template_info === 'object'
          ? character.template_info
          : null,
    template_id: typeof templateId === 'string' || typeof templateId === 'number' ? templateId : null,
    temperament: character.temperament || character.base_temperament || null,
    background: normalizeLegend(
      character.background || character.legend || character.childhood_memory
    ),
    skills: Array.isArray(character.skills) ? character.skills : [],
    traits: Array.isArray(character.traits) ? character.traits : [],
    preferences: Array.isArray(character.preferences) ? character.preferences : [],
    phobias: Array.isArray(character.phobias) ? character.phobias : [],
    appearance:
      character.appearance && typeof character.appearance === 'object'
        ? character.appearance
        : {},
  };
};

// ---------- Валидация ----------

export const validateStep = (stepName, draft) => {
  if (!draft) return 'Черновик не инициализирован';
  switch (stepName) {
    case 'race':
      return draft.race ? null : 'Выберите расу';
    case 'template':
      if (!draft.character_template) return 'Выберите шаблон';
      if (!draft.temperament) return 'Выберите темперамент';
      return null;
    case 'identity':
      if (!draft.name?.trim()) return 'Введите имя персонажа';
      if (isLegendEmpty(draft.background)) {
        return 'Введите или сгенерируйте предысторию персонажа';
      }
      return null;
    case 'skills':
      if (!Array.isArray(draft.skills) || draft.skills.length === 0) {
        return 'Выберите хотя бы один навык';
      }
      if (draft.skills.length > 7) return 'Максимум 7 навыков';
      return null;
    case 'finalize':
      return null;
    default:
      return null;
  }
};

export const validateDraft = (draft) => {
  if (!draft) return ['Черновик пуст'];
  const errors = [];
  if (!draft.name?.trim()) errors.push('Имя обязательно');
  if (!draft.race && !draft.race_id) errors.push('Раса обязательна');
  if (!draft.character_template && !draft.template_id) errors.push('Шаблон обязателен');
  if (!draft.temperament) errors.push('Темперамент обязателен');
  if (isLegendEmpty(draft.background)) errors.push('Предыстория пуста');
  if (!Array.isArray(draft.skills) || draft.skills.length === 0) {
    errors.push('Не выбраны навыки');
  }
  return errors;
};

// ---------- Slice ----------

const initialState = {
  mode: MODE_CREATE,           // 'create' | 'edit'
  activeStep: 0,               // для мастера создания
  draft: createEmptyDraft(),   // активный черновик
  original: null,              // снимок для isDirty в режиме edit

  stepErrors: {},              // { [stepName]: 'message' }
  validationErrors: [],        // массив строк (для редактора)
  submitError: null,

  generationLoading: false,
  generationError: null,
  generationSuccess: null,

  initialized: false,
};

const characterDraftSlice = createSlice({
  name: 'characterDraft',
  initialState,

  reducers: {
    /** Начать мастер создания с нуля. */
    startCreation: (state) => {
      state.mode = MODE_CREATE;
      state.activeStep = 0;
      state.draft = createEmptyDraft();
      state.original = null;
      state.stepErrors = {};
      state.validationErrors = [];
      state.submitError = null;
      state.generationError = null;
      state.generationSuccess = null;
      state.initialized = true;
    },

    /** Загрузить персонажа в редактор. */
    startEditing: (state, action) => {
      const draft = draftFromCharacter(action.payload);
      state.mode = MODE_EDIT;
      state.activeStep = 0;
      state.draft = draft;
      state.original = JSON.parse(JSON.stringify(draft));
      state.stepErrors = {};
      state.validationErrors = [];
      state.submitError = null;
      state.generationError = null;
      state.generationSuccess = null;
      state.initialized = true;
    },

    /** Полный сброс. */
    resetDraft: () => initialState,

    /** Перейти на шаг мастера. */
    goToStep: (state, action) => {
      const step = action.payload;
      if (typeof step === 'number' && step >= 0 && step < CREATION_STEPS.length) {
        state.activeStep = step;
        state.stepErrors = {};
      }
    },

    /** Универсальный патч черновика. */
    patchDraft: (state, action) => {
      const patch = { ...(action.payload || {}) };
      if ('background' in patch) {
        patch.background = normalizeLegend(patch.background);
      }
      state.draft = { ...state.draft, ...patch };
    },

    /** Установить одно поле. */
    setField: (state, action) => {
      const { field, value } = action.payload || {};
      if (!field) return;
      if (field === 'background') {
        state.draft.background = normalizeLegend(value);
      } else {
        state.draft[field] = value;
      }
    },

    /** Выбрать расу (сбрасывает зависимые поля). */
    setRace: (state, action) => {
      const race = action.payload;
      state.draft.race = race || null;
      state.draft.race_id = race?.id || null;
      state.draft.character_template = null;
      state.draft.template_id = null;
      state.draft.temperament = null;
      state.draft.background = emptyLegend();
      state.stepErrors = {};
    },

    /** Выбрать шаблон + темперамент. */
    setTemplateAndTemperament: (state, action) => {
      const { template, temperament } = action.payload || {};
      state.draft.character_template = template || null;
      state.draft.template_id = template?.id || null;
      state.draft.temperament =
        temperament || template?.temperament || 'adaptive';
    },

    /** Навыки. */
    setSkills: (state, action) => {
      state.draft.skills = Array.isArray(action.payload) ? action.payload : [];
    },

    /** Имя. */
    setName: (state, action) => {
      state.draft.name = action.payload || '';
    },

    /** Гендер. */
    setGender: (state, action) => {
      state.draft.gender = action.payload || 'neutral';
    },

    /** Ошибка шага мастера. */
    setStepError: (state, action) => {
      const { step, message } = action.payload || {};
      if (step) state.stepErrors[step] = message;
    },
    clearStepError: (state, action) => {
      if (action.payload) delete state.stepErrors[action.payload];
    },

    /** Ошибки редактора. */
    setValidationErrors: (state, action) => {
      state.validationErrors = Array.isArray(action.payload)
        ? action.payload
        : [];
    },
    clearValidationErrors: (state) => {
      state.validationErrors = [];
    },

    /** Ошибка сабмита (сеть). */
    setSubmitError: (state, action) => {
      state.submitError = action.payload || null;
    },
    clearSubmitError: (state) => {
      state.submitError = null;
    },

    /** Генерация: pending / success / error. */
    generationStarted: (state) => {
      state.generationLoading = true;
      state.generationError = null;
      state.generationSuccess = null;
    },
    generationSucceeded: (state, action) => {
      state.generationLoading = false;
      state.generationError = null;
      state.generationSuccess = 'Имя и легенда успешно сгенерированы';
      const payload = action.payload || {};
      if (typeof payload.name === 'string') state.draft.name = payload.name;
      if (typeof payload.gender === 'string') state.draft.gender = payload.gender;
      if (payload.legend) {
        state.draft.background = normalizeLegend(payload.legend);
      }
    },
    generationFailed: (state, action) => {
      state.generationLoading = false;
      state.generationError = action.payload || 'Ошибка генерации';
    },

    /** Сбросить статус генерации. */
    clearGenerationStatus: (state) => {
      state.generationLoading = false;
      state.generationError = null;
      state.generationSuccess = null;
    },

    /** После успешного сохранения: сбросить isDirty. */
    markSaved: (state, action) => {
      const saved = action.payload;
      if (saved && typeof saved === 'object') {
        state.draft.id = saved.id || saved.image_bit_id || state.draft.id;
      }
      state.original = JSON.parse(JSON.stringify(state.draft));
      state.validationErrors = [];
      state.submitError = null;
    },

    /** Откатить черновик редактора к original. */
    revertDraft: (state) => {
      if (state.original) {
        state.draft = JSON.parse(JSON.stringify(state.original));
      }
      state.validationErrors = [];
      state.submitError = null;
    },
  },
});

export const {
  startCreation,
  startEditing,
  resetDraft,
  goToStep,
  patchDraft,
  setField,
  setRace,
  setTemplateAndTemperament,
  setSkills,
  setName,
  setGender,
  setStepError,
  clearStepError,
  setValidationErrors,
  clearValidationErrors,
  setSubmitError,
  clearSubmitError,
  generationStarted,
  generationSucceeded,
  generationFailed,
  clearGenerationStatus,
  markSaved,
  revertDraft,
} = characterDraftSlice.actions;

// ---------- Селекторы ----------

export const selectDraft = (state) => state.characterDraft.draft;
export const selectDraftMode = (state) => state.characterDraft.mode;
export const selectActiveStep = (state) => state.characterDraft.activeStep;
export const selectCurrentStepName = (state) =>
  CREATION_STEPS[state.characterDraft.activeStep] || 'race';
export const selectStepErrors = (state) => state.characterDraft.stepErrors;
export const selectValidationErrors = (state) =>
  state.characterDraft.validationErrors;
export const selectSubmitError = (state) => state.characterDraft.submitError;
export const selectGenerationState = (state) => ({
  loading: state.characterDraft.generationLoading,
  error: state.characterDraft.generationError,
  success: state.characterDraft.generationSuccess,
});

export const selectIsDirty = (state) => {
  const { original, draft } = state.characterDraft;
  if (!original || !draft) return false;
  return JSON.stringify(original) !== JSON.stringify(draft);
};

export const selectCurrentStepValid = (state) =>
  validateStep(selectCurrentStepName(state), selectDraft(state)) === null;

export default characterDraftSlice.reducer;
